import { useConfirmation } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { useDispatch, useSelector, useShallowEqualSelector } from 'modules/react-redux';
import { getRequest } from 'pages/requestForm/selectors/pageSelectors';
import { useCallback, useEffect, useMemo } from 'react';

import {
    changeQbooksAccountLineItemMatching,
    changeQbooksLineItemMatching,
    updateQBooksFormWithContext,
} from '../../../actions';
import { getQBooksAccountLineItems, getQBooksContext, getQBooksLineItems } from '../../../selectors/qbooks';
import { messages } from './QBooksBillForm.messages';
import type { OnChangeFieldRequiredForMatching, OnMatchingChange } from './QBooksBillForm.types';

export const useQbooksBillFormQbooksContext = () => {
    const dispatch = useDispatch();
    const qbooksContext = useSelector(getQBooksContext);

    const qbooksEnabledLineItemType = qbooksContext.qbooksEnabledLineItemType;

    useEffect(() => {
        dispatch(updateQBooksFormWithContext());
    }, [dispatch, qbooksContext]);

    return {
        qbooksEnabledLineItemType,
    };
};

export const useQbooksBillFormQbooksConditions = ({
    qbooksEnabledLineItemType,
}: {
    qbooksEnabledLineItemType: domain.TemplateSettingsQBooksEnabledLineItemType;
}) => {
    const dispatch = useDispatch();
    const { confirmation } = useConfirmation({
        confirmMessage: messages.removeMatchingConfirmation,
        confirmButtonMessage: messages.confirmChanges,
        // this is important to prevent auto-opening dropdowns again
        // after closing confirmation popup
        returnFocus: false,
    });

    const request = useSelector(getRequest<domain.QBooksBillRequest>);

    const isAccountBasedLinesEnabled =
        qbooksEnabledLineItemType === domain.TemplateSettingsQBooksEnabledLineItemType.All ||
        qbooksEnabledLineItemType === domain.TemplateSettingsQBooksEnabledLineItemType.AccountBasedLines;
    const isItemBasedLinesEnabled =
        qbooksEnabledLineItemType === domain.TemplateSettingsQBooksEnabledLineItemType.All ||
        qbooksEnabledLineItemType === domain.TemplateSettingsQBooksEnabledLineItemType.ItemBasedLines;

    const { accountLineItems, lineItems } = useShallowEqualSelector((state) => {
        const accountLineItems = isAccountBasedLinesEnabled ? getQBooksAccountLineItems(state, request) : [];
        const lineItems = isItemBasedLinesEnabled ? getQBooksLineItems(state, request) : [];

        return {
            accountLineItems,
            lineItems,
        };
    });

    const hasMatchedLines = useMemo(
        () => [...accountLineItems, ...lineItems].some((line) => Boolean(line.matchedLineItemId)),
        [accountLineItems, lineItems]
    );

    const removeAllMatching = useCallback(() => {
        accountLineItems.forEach((line) => {
            if (line.matchedLineItemId) {
                dispatch(changeQbooksAccountLineItemMatching(line.id));
            }
        });
        lineItems.forEach((line) => {
            if (line.matchedLineItemId) {
                dispatch(changeQbooksLineItemMatching(line.id));
            }
        });
    }, [accountLineItems, dispatch, lineItems]);

    const changeLineMatching = useCallback<OnMatchingChange>(
        (lineId, matchedLineItemId, isAccountLine) => {
            if (isAccountLine) {
                dispatch(changeQbooksAccountLineItemMatching(lineId, matchedLineItemId));
            } else {
                dispatch(changeQbooksLineItemMatching(lineId, matchedLineItemId));
            }
        },
        [dispatch]
    );

    const handleMatchingRelatedFieldChange = useCallback<OnChangeFieldRequiredForMatching>(
        (callback, { lineId, isAccountLine } = { lineId: undefined }) => {
            const lines = lineId ? (isAccountLine ? accountLineItems : lineItems) : [];
            const line = lineId ? lines.find((line) => line.id === lineId) : null;
            const lineMatched = line ? typeof line.matchedLineItemId === 'string' : false;

            if (lineId ? !lineMatched : !hasMatchedLines) {
                callback();

                return Promise.resolve();
            }

            // at least for `DateTimeEditor` we need to revert
            // state to origin manually on "cancel", that's why we omit `catch` at this level
            return confirmation().then(() => {
                callback();

                if (lineId) {
                    changeLineMatching(lineId, undefined, isAccountLine);
                } else {
                    removeAllMatching();
                }
            });
        },
        [accountLineItems, confirmation, hasMatchedLines, lineItems, removeAllMatching, changeLineMatching]
    );

    return {
        isAccountBasedLinesEnabled,
        isItemBasedLinesEnabled,
        accountLineItems,
        lineItems,
        hasMatchedLines,
        removeAllMatching,
        changeLineMatching,
        handleMatchingRelatedFieldChange,
    };
};
